"use client";

import { useTranslation } from "react-i18next";

import { type AppContext } from "@/types";
import * as events from "@/events";
import Button from "@/shared/ui/components/button";
import Box from "@/shared/ui/components/box";
import Modal from "@/shared/ui/components/modal";
import MoneyAmount from "@/shared/ui/components/money-amount";
import { CashbackBadgeIcon } from "@/shared/ui/icons/cashback-badge-icon";

interface CashbackDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  cartValue: number;
  cashbackAmount: number;
  cashbackPercent?: number;
  ctx: AppContext;
  currency?: string;
}

export function CashbackDetailsModal({
  isOpen,
  onClose,
  cartValue,
  cashbackAmount,
  cashbackPercent,
  currency = "SAR",
  ctx,
}: CashbackDetailsModalProps) {
  const { t, i18n } = useTranslation();

  if (!isOpen) return null;

  return (
    <Modal
      onClose={onClose}
      showCloseButton
      stickyFooter={(close) => (
        <Button
          size="large"
          onClick={() => {
            ctx.sendEvent(events.CLOSE_MODAL_CLICKED, {
              modal_name: "cashback",
            });
            close();
          }}
          className="w-full"
        >
          {t("cashback.got_it")}
        </Button>
      )}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex flex-col max-h-[85vh]"
      >
        <div className="overflow-y-auto flex-1">
          <div>
            <div className="px-6 space-y-6">
              <div className="text-center">
                <div className="flex mb-6 items-center justify-center">
                  <CashbackBadgeIcon className="mt-6" />
                </div>
                <h1 className="text-[24px] font-semibold text-[#16181D] mb-2">
                  <span className="mr-2 rtl:mr-0 rtl:ml-0.5">
                    {t("cashback.title_prefix")}
                  </span>
                  <span className="">{cashbackPercent}</span>
                  <span className="mr-1 rtl:ml-0 rtl:mr-0.5">
                    {t("cashback.title_suffix")}
                  </span>
                </h1>
              </div>
              <Box radius="md" className="mb-4 p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">
                    {t("cashback.order_total")}
                  </span>
                  <MoneyAmount
                    amount={cartValue}
                    currency={currency}
                    className="text-sm font-medium"
                    language={i18n.language}
                  />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-green-600 font-medium">
                    {t("cashback.you_will_earn")}
                  </span>
                  <MoneyAmount
                    amount={cashbackAmount}
                    currency={currency}
                    className="text-sm font-semibold text-green-600"
                    language={i18n.language}
                  />
                </div>
              </Box>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
